import type { SharedPreviewItemDto } from '@/apis/backend/generated/models';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, createFileRoute } from '@tanstack/react-router';
import { formatSubjectRange } from '@/sections/public-preview/share-subject';
import { usePublicEmailPreviewControllerListMyShares } from '@/apis/backend/generated/email-preview-public/email-preview-public';

import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Box,
  Card,
  Chip,
  Stack,
  Paper,
  Container,
  Typography,
  CardActionArea,
  CircularProgress,
} from '@mui/material';

export const Route = createFileRoute('/p/')({
  component: SharedWithMePage,
});

const SESSION_STORAGE_KEY = 'nexmail_preview_session';

function readSession(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function SharedWithMePage() {
  const { t } = useTranslation('common');
  // Read once on mount; the global session token is written by the per-share
  // page on sign-in. There's no token-less sign-in here, so an empty session
  // just means "open a preview link you were sent to sign in first".
  const [sessionToken] = useState<string | null>(() => readSession());

  const sessionHeader: Record<string, string> = sessionToken
    ? { 'x-preview-session': sessionToken }
    : {};

  const sharesQuery = usePublicEmailPreviewControllerListMyShares({
    query: {
      enabled: !!sessionToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    request: { headers: sessionHeader },
  });

  useEffect(() => {
    document.title = t('sharedWithMe.title', 'Shared with me');
  }, [t]);

  if (!sessionToken) {
    return (
      <CenteredCard>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t('sharedWithMe.signInTitle', 'Sign in to see previews shared with you')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'sharedWithMe.signInBody',
            'Open any preview link you were sent and sign in there. Once signed in, every preview shared with you will show up here.',
          )}
        </Typography>
      </CenteredCard>
    );
  }

  if (sharesQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // A 403 here means the session expired or was cleared server-side.
  if (sharesQuery.error) {
    return (
      <CenteredCard>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t('sharedWithMe.signedOutTitle', 'Your session has expired')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'sharedWithMe.signedOutBody',
            'Open a preview link you were sent to sign in again, then come back here.',
          )}
        </Typography>
      </CenteredCard>
    );
  }

  const items = sharesQuery.data?.items ?? [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('sharedWithMe.heading', 'Shared with me')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t(
            'sharedWithMe.subheading',
            'Emails and plans you can review and comment on.',
          )}
        </Typography>

        {items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('sharedWithMe.emptyTitle', 'Nothing shared with you yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                'sharedWithMe.emptyBody',
                "Previews you're invited to review will appear here.",
              )}
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {items.map(item => (
              <SharePreviewCard key={item.shareToken} item={item} />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function SharePreviewCard({ item }: { item: SharedPreviewItemDto }) {
  const { t } = useTranslation('common');
  const isPeriod = item.subjectType !== 'email';
  const range = formatSubjectRange(item.subjectId);
  const title = isPeriod
    ? range ??
      (item.subjectType === 'plan'
        ? t('sharedWithMe.plan', 'Campaign plan')
        : t('sharedWithMe.period', 'A period of emails'))
    : item.subjectLine?.trim() || item.documentName || t('sharedWithMe.untitled', 'Email preview');
  const sharedOn = new Date(item.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <CardActionArea
        component={Link}
        to={`/p/${item.shareToken}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box
          sx={{
            aspectRatio: '4 / 3',
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {item.thumbnailUrl && !isPeriod ? (
            <Box
              component="img"
              src={item.thumbnailUrl}
              alt={title}
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
            />
          ) : (
            <Stack sx={{ height: '100%', justifyContent: 'center' }}>
              {isPeriod ? (
                <CalendarMonthIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              ) : (
                <MailOutlineIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              )}
            </Stack>
          )}
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {item.brandName}
            </Typography>
            {isPeriod && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  item.subjectType === 'plan'
                    ? t('sharedWithMe.planChip', 'Plan')
                    : t('sharedWithMe.periodChip', 'Period')
                }
                sx={{ textTransform: 'none', height: 20 }}
              />
            )}
          </Stack>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('sharedWithMe.sharedOn', 'Shared {{date}}', { date: sharedOn })}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }} elevation={2}>
        {children}
      </Paper>
    </Box>
  );
}
